import {
  REVIEWABLE_DOCUMENT_TYPES,
  REVIEWABLE_DOCUMENT_TYPE_PREFIX,
  ReviewableDocumentInput,
} from '@bordrio/shared';
import clsx from 'clsx';
import {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FileInput from '@src/components/FileInput';

interface Props<
  ValueType extends FieldValues,
  PathType extends FieldPath<ValueType>,
> {
  className?: string;
  control: FieldPathValue<ValueType, PathType> extends ReviewableDocumentInput
    ? Control<ValueType>
    : never;
  name: PathType;
  horizontalLayout?: boolean;
  labelFileOne?: string;
  labelFileTwo?: string;
}

function ReviewableDocumentFileInput<
  ValueType extends FieldValues,
  PathType extends FieldPath<ValueType>,
>({
  className,
  control,
  name,
  horizontalLayout,
  labelFileOne,
  labelFileTwo,
}: Props<ValueType, PathType>): JSX.Element {
  const { t } = useTranslation();
  const manualTranslate = t;

  // a hack to allow proper typings for the control
  const controlTyped = control as unknown as Control<{
    prefix: ReviewableDocumentInput;
  }>;
  const prefix = name as 'prefix';

  const documentTypeId = useWatch({
    control: controlTyped,
    name: `${prefix}.currentVersion.documentTypeId`,
  });

  const documentType = Object.values(REVIEWABLE_DOCUMENT_TYPES).find(
    (type) => type.id === documentTypeId,
  );

  if (!documentType) {
    return (
      <div>
        {t(
          'validation.unknown_document_type',
          'Unknown document type. Please contact Bordr support.',
        )}
      </div>
    );
  }

  const documentTypeName =
    documentTypeId === REVIEWABLE_DOCUMENT_TYPES.other_additional_document.id
      ? t(
          `translation:reviewable_documents.other_additional_document`,
          'Document',
        )
      : manualTranslate(
          `${REVIEWABLE_DOCUMENT_TYPE_PREFIX}.${documentType.key}`,
          documentType.name,
        );

  return (
    <div
      className={clsx(
        horizontalLayout
          ? 'grid grid-cols-1 gap-8 sm:grid-cols-2'
          : 'flex flex-col gap-4',
        className,
      )}
    >
      <FileInput.LabelledController
        category="reviewableDocumentVersionFileOne"
        control={controlTyped}
        label={
          labelFileOne ??
          (documentType.hasTwoFiles
            ? `${documentTypeName} (${t(
                'translation:reviewable_documents.front',
                'Front',
              )})`
            : documentTypeName)
        }
        name={`${prefix}.currentVersion.fileOne`}
      />
      {documentType.hasTwoFiles && (
        <FileInput.LabelledController
          category="reviewableDocumentVersionFileTwo"
          control={controlTyped}
          label={
            labelFileTwo ??
            `${documentTypeName} (${t(
              'translation:reviewable_documents.back',
              'Back',
            )})`
          }
          name={`${prefix}.currentVersion.fileTwo`}
        />
      )}
    </div>
  );
}

export default ReviewableDocumentFileInput;
